import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// import { useSettings } from '../../contexts/SettingsContext'; // Unused import
import { ThemeToggle } from '../ThemeToggle';
import { ViewingAsBanner } from '../common/ViewingAsBanner';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as InitiativeIcon,
  ExitToApp as LogoutIcon,
  TrendingUp as TrendingUpIcon,
  AccountTree as SocialIcon,
  // Search as SearchIcon, // Unused import - search tab hidden
  Task as TaskIcon,
  // ViewModule as BoardIcon, // Unused import
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Mail as MailIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const drawerWidth = 280;

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOut } = useAuth();
  // const { hideBoardTab } = useSettings(); // Unused variable
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', badge: 0 },
    { text: 'Activity', icon: <TrendingUpIcon />, path: '/activity', badge: 0 },
    { text: 'Contacts', icon: <PeopleIcon />, path: '/contacts', badge: 0 },
    { text: 'Email', icon: <MailIcon />, path: '/email', badge: 0 },
    { text: 'Organizations', icon: <BusinessIcon />, path: '/organizations', badge: 0 },
    { text: 'Initiatives', icon: <InitiativeIcon />, path: '/initiatives', badge: 0 },
    { text: 'Tasks', icon: <TaskIcon />, path: '/tasks', badge: 0 },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', badge: 0 },
    { text: 'Galaxy', icon: <SocialIcon />, path: '/graph', badge: 0 },
    // { text: 'Search', icon: <SearchIcon />, path: '/search', badge: 0 }, // Hidden
    { text: 'Profile', icon: <PersonIcon />, path: '/profile', badge: 0 },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', badge: 0 },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2, py: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          onClick={() => navigate('/dashboard')}
        >
          <Box
            component="img"
            src="/praytell_logo.png"
            alt="PrayTell"
            sx={{
              height: 32,
              filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            Catalyze
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, px: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 163, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 163, 255, 0.15)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
              {item.badge > 0 && (
                <ListItemSecondaryAction>
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 3,
                      },
                    }}
                  />
                </ListItemSecondaryAction>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleSignOut}
            sx={{
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {isMobile && (
            <Box
              component="img"
              src="/praytell_logo.png"
              alt="PrayTell"
              sx={{
                height: 32,
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
              onClick={() => navigate('/dashboard')}
            />
          )}
          {title && location.pathname !== '/dashboard' && (
            <Typography variant="h6" sx={{ fontWeight: 100, color: 'text.primary' }}>
              {title}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <ThemeToggle />
          <IconButton
            color="inherit"
            onClick={handleSignOut}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                backgroundColor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                backgroundColor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
          <ViewingAsBanner />
          {children}
        </Box>
      </Box>
    </Box>
  );
} 